export type DashboardWidgetSize = 'small' | 'medium' | 'wide' | 'full';
export type DashboardWidgetType = 'portfolio' | 'cameras' | 'activity' | 'device_control' | 'map' | 'device_health' | 'quick_add' | 'push' | 'billing' | 'properties' | 'customers';

export type DashboardLayoutWidget = {
  type: DashboardWidgetType;
  size: DashboardWidgetSize;
  x?: number;
  y?: number;
  gridW?: number;
  gridH?: number;
};

const SIZE_WIDTH: Record<DashboardWidgetSize, number> = { small: 3, medium: 6, wide: 9, full: 12 };
const DEFAULT_HEIGHT: Record<DashboardWidgetType, number> = {
  portfolio: 2, cameras: 5, activity: 6, device_control: 3, map: 6, device_health: 3,
  quick_add: 3, push: 3, billing: 3, properties: 3, customers: 3,
};

/** Minimum cells required for the widget's controls/content to remain usable. */
export const WIDGET_MINIMUMS: Record<DashboardWidgetType, { width: number; height: number }> = {
  portfolio: { width: 6, height: 2 }, cameras: { width: 6, height: 4 }, activity: { width: 5, height: 4 },
  device_control: { width: 4, height: 3 }, map: { width: 6, height: 5 }, device_health: { width: 3, height: 3 },
  quick_add: { width: 3, height: 3 }, push: { width: 3, height: 3 }, billing: { width: 3, height: 3 },
  properties: { width: 4, height: 3 }, customers: { width: 4, height: 3 },
};

export const widgetPresetWidth = (size: DashboardWidgetSize) => SIZE_WIDTH[size];

export function clampDashboardWidget<T extends DashboardLayoutWidget>(widget: T): T {
  const minimum = WIDGET_MINIMUMS[widget.type];
  const width = Math.min(12, Math.max(minimum.width, widget.gridW ?? SIZE_WIDTH[widget.size]));
  const height = Math.min(12, Math.max(minimum.height, widget.gridH ?? DEFAULT_HEIGHT[widget.type]));
  const x = Math.max(1, Math.min(13 - width, widget.x ?? 1));
  return { ...widget, gridW: width, gridH: height, x, y: Math.max(1, widget.y ?? 1) };
}

/** Migrates previously saved layouts whose free resizing allowed unusably small cards. */
export function migrateDashboardLayout<T extends DashboardLayoutWidget>(widgets: T[]): T[] {
  return widgets.map(clampDashboardWidget);
}
